import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import HamburgerMenu from './HamburgerMenu';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        border: isDark ? '#444' : '#dee2e6',
        buttonBg: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        approveBg: isDark ? '#4caf50' : '#28a745',
        denyBg: isDark ? '#f44336' : '#dc3545',
        pendingBg: isDark ? '#3d3200' : '#fff3cd',
        pendingText: isDark ? '#ffd54f' : '#856404',
    };

    return {
        container: {
            width: '90%',
            maxWidth: '1100px',
            margin: '20px auto',
            fontFamily: 'sans-serif',
            backgroundColor: colors.background,
            color: colors.text,
        },
        pageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            gap: '12px',
            flexWrap: 'wrap',
        },
        pageTitle: { fontSize: '24px', margin: 0 },
        table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
        th: { borderBottom: `2px solid ${colors.border}`, padding: '12px', textAlign: 'left' },
        td: { borderBottom: `1px solid ${colors.border}`, padding: '12px', verticalAlign: 'top' },
        button: {
            padding: '8px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '8px',
            color: colors.buttonText,
            backgroundColor: colors.buttonBg,
        },
        approveButton: {
            padding: '8px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '8px',
            color: '#fff',
            backgroundColor: colors.approveBg,
        },
        denyButton: {
            padding: '8px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#fff',
            backgroundColor: colors.denyBg,
        },
        statusBadge: (status) => ({
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'capitalize',
            backgroundColor: status === 'pending' ? colors.pendingBg : colors.border,
            color: status === 'pending' ? colors.pendingText : colors.text,
        }),
        link: { color: colors.buttonBg, textDecoration: 'none' },
        empty: { padding: '24px', textAlign: 'center', color: colors.text, opacity: 0.8 },
        filterRow: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
        select: {
            padding: '8px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.background,
            color: colors.text,
        },
    };
};

function AccountRequestsPage({ theme }) {
    const styles = getStyles(theme);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [actionId, setActionId] = useState(null);

    const fetchRequests = useCallback(() => {
        setLoading(true);
        api.get('/account-requests/')
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setRequests(data);
            })
            .catch((err) => {
                console.error('Error fetching account requests:', err);
                setRequests([]);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const filteredRequests = requests.filter((request) => (
        statusFilter === 'all' ? true : request.status === statusFilter
    ));

    const handleApprove = (id) => {
        if (!window.confirm('Approve this account request and email login credentials to the user?')) {
            return;
        }
        setActionId(id);
        api.post(`/account-requests/${id}/approve/`)
            .then(() => fetchRequests())
            .catch((err) => {
                const message = err?.response?.data?.detail || 'Unable to approve this request.';
                alert(message);
            })
            .finally(() => setActionId(null));
    };

    const handleDeny = (id) => {
        if (!window.confirm('Deny this account request and notify the user by email?')) {
            return;
        }
        setActionId(id);
        api.post(`/account-requests/${id}/deny/`)
            .then(() => fetchRequests())
            .catch((err) => {
                const message = err?.response?.data?.detail || 'Unable to deny this request.';
                alert(message);
            })
            .finally(() => setActionId(null));
    };

    return (
        <div style={styles.container}>
            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme} />
                <h1 style={styles.pageTitle}>Account Requests</h1>
                <div style={styles.filterRow}>
                    <label htmlFor="statusFilter">Show:</label>
                    <select
                        id="statusFilter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={styles.select}
                    >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                        <option value="all">All</option>
                    </select>
                    <Link to="/manage-users" style={styles.link}>Manage Users</Link>
                </div>
            </div>

            {loading ? (
                <div style={styles.empty}>Loading account requests...</div>
            ) : filteredRequests.length === 0 ? (
                <div style={styles.empty}>No account requests found.</div>
            ) : (
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Email</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Submitted</th>
                            <th style={styles.th}>Reviewed</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRequests.map((request) => (
                            <tr key={request.id}>
                                <td style={styles.td}>{request.first_name} {request.last_name}</td>
                                <td style={styles.td}>{request.email}</td>
                                <td style={styles.td}>
                                    <span style={styles.statusBadge(request.status)}>{request.status}</span>
                                </td>
                                <td style={styles.td}>
                                    {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                                </td>
                                <td style={styles.td}>
                                    {request.reviewed_at ? new Date(request.reviewed_at).toLocaleString() : '—'}
                                    {request.reviewed_by_username ? (
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                            by {request.reviewed_by_username}
                                        </div>
                                    ) : null}
                                    {request.created_username ? (
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                            user: {request.created_username}
                                        </div>
                                    ) : null}
                                </td>
                                <td style={styles.td}>
                                    {request.status === 'pending' ? (
                                        <>
                                            <button
                                                style={styles.approveButton}
                                                disabled={actionId === request.id}
                                                onClick={() => handleApprove(request.id)}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                style={styles.denyButton}
                                                disabled={actionId === request.id}
                                                onClick={() => handleDeny(request.id)}
                                            >
                                                Deny
                                            </button>
                                        </>
                                    ) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default AccountRequestsPage;
